"use client";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Network,
  Clock,
  AlertOctagon,
} from "lucide-react";

interface ChartDataItem {
  name: string;
  value: number;
  color: string;
}

interface TrafficData {
  total_sent: number;
  total_delivered: number;
  total_pending: number;
  total_failed: number;
  total_absent_from_network: number;
  invalid: number;
  total_delivered_to_network: number;
}

interface BarChartProps {
  trafficData: TrafficData;
}

const TrafficBarChart = ({ trafficData }: BarChartProps) => {
  const chartData: ChartDataItem[] = [
    { name: "Total Sent", value: trafficData.total_sent, color: "#6b7280" },
    { name: "Delivered", value: trafficData.total_delivered, color: "#10b981" },
    { name: "Pending", value: trafficData.total_pending, color: "#3b82f6" },
    { name: "Failed", value: trafficData.total_failed, color: "#ef4444" },
    {
      name: "Absent",
      value: trafficData.total_absent_from_network,
      color: "#f59e0b",
    },
    { name: "Invalid", value: trafficData.invalid, color: "#8b5cf6" },
    {
      name: "To Network",
      value: trafficData.total_delivered_to_network,
      color: "#06b6d4",
    },
  ];

  // Format numbers
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  // Custom tooltip formatter for charts
  const customTooltipFormatter = (value: number): string => {
    return formatNumber(value);
  };

  const getIcon = (index: number) => {
    const icons = [
      <Users key="user" className="h-5 w-5 text-white" />,
      <CheckCircle key="check" className="h-5 w-5 text-white" />,
      <Clock key="clock" className="h-5 w-5 text-white" />,
      <XCircle key="x" className="h-5 w-5 text-white" />,
      <AlertTriangle key="alert" className="h-5 w-5 text-white" />,
      <AlertOctagon key="octagon" className="h-5 w-5 text-white" />,
      <Network key="network" className="h-5 w-5 text-white" />,
    ];
    return icons[index];
  };

  return (
    <div className="overflow-hidden bg-white rounded-xl shadow-lg border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-8 w-1 bg-red-600 rounded-full"></div>
        <h2 className="text-xl font-semibold text-gray-900">
          Traffic Distribution
        </h2>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
            <XAxis
              dataKey="name"
              tick={{ fill: "#6b7280", fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis tick={{ fill: "#6b7280" }} />
            <Tooltip
              formatter={(value: any) =>
                customTooltipFormatter(value as number)
              }
              contentStyle={{
                backgroundColor: "white",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            />
         
            <Bar dataKey="value" name="Count" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrafficBarChart;
